import { PrimarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import DataIsReprocessingBar from 'components/ReusableComponents/DataIsReprocessingBar'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router'
import { Link } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import ReactTooltip from 'react-tooltip'
import billingApi from 'services/api/billing'

import {
  selectCurrentHotelName,
  selectIsInternalAdmin,
  selectIsInvoiceGroupView
} from '../../accessors/index'
import {
  getInvoiceContacts,
  getInvoicesList,
  getPaymentsList,
  postPaymentRetry,
  updatePayment
} from '../../actions/billing'
import BillingContactsDialog from '../../components/ReusableComponents/BillingContactsDialog/index'
import ConfirmationDialog from '../../components/ReusableComponents/ConfirmationDialog/index'
import CustomHeader from '../../components/ReusableComponents/HeaderTitles/index'
import LoadingIndicator from '../../components/ReusableComponents/LoadingIndicator'
import MarkAsPaid from '../../components/ReusableComponents/MarkAsPaidDialog/index'
import Toggle from '../../components/ReusableComponents/Toggle/index'
import { getUSDstring } from '../../utils/helpers'
import Balance from './Balance/index'
import styles from './BillingNew.module.scss'
import BillingRowHolder from './BillingRowHolder/index'
import FindHotelByInvoiceIdDialog from './FindHotelByInvoiceIdDialog'
import InvoiceRow from './InvoiceRow'
import ManagePaymentMethods from './ManagePaymentMethods/index'
import { PaymentRow } from './PaymentRow'
import { invoiceStatusesAcceptableForTotalDueCalculation } from './statusNames'

const adminOnlyStatuses = [
  'ReviewPending',
  'MonthInProgress',
  'ReviewCompleted',
  'MonthSkipped'
]

const BillingNew = () => {
  const invoices = useSelector(s => s.billing.invoices)
  const payments = useSelector(s => s.billing.payments)
  const isInvoicesLoading = useSelector(s => s.billing.isGettingInvoices)
  const isPaymentsLoading = useSelector(s => s.billing.isGettingPayments)
  const merchantName = useSelector(s => s.billing.merchantName)
  const totalDue = useSelector(s => s.billing.totalDue)
  const balance = useSelector(s => s.billing.currentBalance)
  const activeBalance = useSelector(s => s.billing.activeBalance)
  const currentHotelName = useSelector(selectCurrentHotelName)
  const isInternalAdmin = useSelector(selectIsInternalAdmin)
  const isGroupView = useSelector(selectIsInvoiceGroupView)

  const [isSendingInvoice, setIsSendingInvoice] = useState(false)

  useEffect(() => {
    if (invoices.length > 0) {
      window.invoices = invoices
    }
  }, [invoices])

  const [searchValue, setSearchValue] = useState('')

  const dispatch = useDispatch()

  const { addToast } = useToasts()

  const [showSkippedInvoices, setShowSkippedInvoices] = useState(false)

  useEffect(() => {
    async function getData() {
      await dispatch(getInvoicesList(isGroupView))
      await dispatch(getPaymentsList(isGroupView))
    }

    getData()
  }, [dispatch, isGroupView])

  const [isShowingFindHotelByInvoiceIdDialog, setShowingFindHotelByInvoiceIdDialog] =
    useState(false)

  const [isShowingAddPaymentDialog, setShowingAddPaymentDialog] = useState()

  const [isShowingBillingContactsDialog, setShowingBillingContactsDialog] = useState()

  const billingContacts = useSelector(s => s.billing.billingContacts)

  const isGettingBillingContacts = useSelector(s => s.billing.isGettingBillingContacts)

  const [isShowingInvoiceSent, setShowingInvoiceSent] = useState()

  const didInvoiceSentSuccesfully = useSelector(s => s.billing.didInvoiceSentSuccesfully)

  useEffect(() => {
    if (didInvoiceSentSuccesfully) {
      setShowingInvoiceSent(true)
      setShowingBillingContactsDialog(false)
    }
  }, [didInvoiceSentSuccesfully])

  const {
    params: { editBillingInfo: isEditBillingOn }
  } = useRouteMatch({
    path: '/billing/:editBillingInfo?',
    strict: false
  })

  function handleShowInvoiceContacts() {
    dispatch(getInvoiceContacts(isGroupView))
    setShowingBillingContactsDialog(true)
  }

  function handleSendInvoice(contactData) {
    setIsSendingInvoice(true)

    billingApi
      .sendInvoice(contactData)
      .then(res => {
        addToast(`Invoice is sent to ${res.totalEmailsSent} recipients`, {
          appearance: 'success'
        })
      })
      .catch(err => {
        addToast(err.message, { appearance: 'error' })
      })
      .finally(() => {
        setIsSendingInvoice(false)
      })
  }

  function handleRetryPayment(id) {
    dispatch(postPaymentRetry(id, isGroupView))
  }

  function handlePaymentUpdate(payment) {
    dispatch(updatePayment(payment, isGroupView))
  }

  const paymentsInProgress = payments.filter(p => p.processorStatus === 'InitialApproval')

  const paymentAmountInProgress = paymentsInProgress.reduce(
    (prev, curr) => prev + curr.paymentAmount,
    0
  )

  function getFilteredInvoices() {
    if (invoices) {
      const filteredInvoices = invoices
        .filter(invoice =>
          `${invoice.searchString} #${invoice.subsidiaryInvoiceId}`.match(
            new RegExp(searchValue, 'i')
          )
        )
        .filter(invoice =>
          invoice.status === 'MonthSkipped' ? showSkippedInvoices : true
        )
        .filter(invoice =>
          isInternalAdmin ? true : !adminOnlyStatuses.includes(invoice.status)
        )
        .map(invoice => ({ ...invoice, isPaymentInProgress: false }))

      let remainingPaymentInProgress = paymentAmountInProgress

      const invoiceIdsOfPendingPayments = filteredInvoices
        .slice()
        .reverse()
        .filter(invoice => ['PaidPartially', 'PaymentPending'].includes(invoice.status))
        .map(i => i.subsidiaryInvoiceId)

      invoiceIdsOfPendingPayments?.forEach(invoiceId => {
        if (remainingPaymentInProgress > 0) {
          const invoice = filteredInvoices.find(i => i.subsidiaryInvoiceId === invoiceId)

          remainingPaymentInProgress =
            remainingPaymentInProgress - invoice.totalAmountDueRemaining

          invoice.isPaymentInProgress = true
        }
      })

      return filteredInvoices
    }
  }

  if (isInvoicesLoading || isPaymentsLoading) {
    return <LoadingIndicator />
  }

  return (
    <div className={styles.wrapper}>
      {isShowingFindHotelByInvoiceIdDialog && (
        <FindHotelByInvoiceIdDialog
          onClose={() => setShowingFindHotelByInvoiceIdDialog(false)}
        />
      )}

      <DataIsReprocessingBar marginBottom={20} />
      <div className={styles.content}>
        <CustomHeader
          title="Billing and payments"
          subtitle={isGroupView ? merchantName : currentHotelName}
          width={1000}
        >
          {isInternalAdmin && (
            <PrimarySmallButton
              style={{ marginLeft: 12 }}
              onClick={() => setShowingFindHotelByInvoiceIdDialog(true)}
            >
              Find hotel with invoice id
            </PrimarySmallButton>
          )}
        </CustomHeader>
        {isEditBillingOn && <ManagePaymentMethods isGroupView={isGroupView} />}
        {isShowingInvoiceSent && (
          <ConfirmationDialog
            title="Invoice sent!"
            onConfirm={() => setShowingInvoiceSent(false)}
          >
            <p>Invoice sent to selected contacts succesfully!</p>
          </ConfirmationDialog>
        )}
        {isShowingFindHotelByInvoiceIdDialog && (
          <FindHotelByInvoiceIdDialog
            onClose={() => setShowingFindHotelByInvoiceIdDialog(false)}
          />
        )}
        {isShowingBillingContactsDialog && (
          <BillingContactsDialog
            contacts={billingContacts}
            handleConfirm={handleSendInvoice}
            isSendingInvoice={isSendingInvoice || isGettingBillingContacts}
            handleHideDialog={() => setShowingBillingContactsDialog(false)}
          />
        )}
        {isShowingAddPaymentDialog && (
          <MarkAsPaid
            title="Add new payment"
            handleClose={() => setShowingAddPaymentDialog(false)}
          />
        )}
        <ReactTooltip />
        <div className={styles.invoicesColumn}>
          <Balance
            balance={balance}
            activeBalance={activeBalance}
            handleAddPaymentClick={() => setShowingAddPaymentDialog(true)}
            handleSendEmailClick={handleShowInvoiceContacts}
            isInternalAdmin={isInternalAdmin}
            isGroupView={isGroupView}
            unpaidInvoiceIds={invoices
              .filter(i =>
                invoiceStatusesAcceptableForTotalDueCalculation.includes(i.status)
              )
              .map(i => i.subsidiaryInvoiceId)}
          />
          <BillingRowHolder
            title={
              isInternalAdmin ? (
                <div className={styles.billingRowTitle}>
                  Total due: {getUSDstring(totalDue)}{' '}
                  <div className={styles.skippedMonthFilter}>
                    <Toggle
                      checked={showSkippedInvoices}
                      handleChange={e => setShowSkippedInvoices(e.target.checked)}
                      label="Show skipped"
                      mini
                    />
                  </div>
                </div>
              ) : (
                'Invoices'
              )
            }
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            isSearchable
          >
            {getFilteredInvoices().map(invoice => (
              <InvoiceRow
                isGroupView={isGroupView}
                key={invoice.subsidiaryInvoiceId}
                {...invoice}
              />
            ))}
          </BillingRowHolder>
          <p className={styles.revisionLink}>
            For any billing concerns, we're here to help. Feel free to reach out via email
            at {` `}
            <a href="mailto:billing@directful.com" target="_blank" rel="noreferrer">
              billing@directful.com
            </a>
            {` `}or give us a call at <a href="tel:+16504497321">+1 (650) 449-7321</a>. If
            you need our W9 form, you can find it for download at{' '}
            <a href="https://directful.com/w9" target="_blank" rel="noreferrer">
              directful.com/w9
            </a>
            .
          </p>
        </div>
        <div className={styles.paymentsColumn}>
          <BillingRowHolder
            title={
              <div className={styles.billingRowTitle}>
                Processed payments
                <Link
                  to="/billing/editBillingInfo"
                  className={styles.billingRowTitleButton}
                >
                  <button>Edit payment methods</button>
                </Link>
              </div>
            }
          >
            {payments &&
              payments.map(p => (
                <PaymentRow
                  {...p}
                  key={p.paymentAddedOn + p.paymentFriendlyName}
                  handleRetry={() => handleRetryPayment(p.id)}
                  handleMarkAsPaid={() =>
                    handlePaymentUpdate({
                      paymentId: p.id,
                      approvalCode: p.paymentConfirmationCode,
                      paymentProcessedBy: p.paymentProcessedBy,
                      processorStatus: 'Success'
                    })
                  }
                  handleMarkAsRejected={() =>
                    handlePaymentUpdate({
                      paymentId: p.id,
                      approvalCode: p.paymentConfirmationCode,
                      paymentProcessedBy: p.paymentProcessedBy,
                      processorStatus: 'Declined'
                    })
                  }
                />
              ))}
          </BillingRowHolder>
        </div>
      </div>
    </div>
  )
}

export default BillingNew
